import React, { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import { RuleAttributes } from '@loyalty/shared-common/rule-api-client';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';

interface AccountRulesContextInterface {
  accountRules: RuleAttributes[];
  fetchAccountRules: (withDeleted?: boolean) => void;
  isLoading: IsLoading;
}

export const AccountRulesContext = createContext<AccountRulesContextInterface>({
  accountRules: [],
  fetchAccountRules: () => undefined,
  isLoading: () => false,
});

export function useAccountRules() {
  return useContext(AccountRulesContext);
}

export function AccountRulesProvider({ children }: { children: ReactNode }) {
  const { currentAccount } = useAccounts();
  const { notify } = useAlerts();
  const { t } = useTranslation();
  const { ruleApi } = useApi();

  const [accountRules, setAccountRules] = useState<RuleAttributes[]>([]);

  const { addLoader, removeLoader, isLoading } = useLoaders();

  const fetchAccountRules = useCallback(
    (withDeleted?: boolean) => {
      addLoader('fetchAccountRules');

      ruleApi
        .getAccountRules(currentAccount.id, { params: { withDeleted } })
        .then(setAccountRules)
        .catch(error => {
          setAccountRules([]);
          notify(t('account-rules.errors.failed-fetch-rules', { error }), 'error');
        })
        .finally(() => removeLoader('fetchAccountRules'));
    },
    [addLoader, currentAccount?.id, notify, removeLoader, ruleApi, t],
  );

  const context = {
    accountRules,
    fetchAccountRules,
    isLoading,
  };

  return <AccountRulesContext.Provider value={context}>{children}</AccountRulesContext.Provider>;
}
